import React, { useMemo, useState } from "react";
import { Alert, Button, Input, Space, Tag, Typography } from "antd";
import {
  CheckCircleFilled,
  MessageOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { toFa } from "../../utils/helpers";
import { parseMobiles } from "./parseMobiles";
import styles from "./styles.module.css";

/* Third step of setup, and the one that decides whether this becomes a team
 * account or a single-player trial.
 *
 * Numbers are added one at a time and shown as chips, so a typo is visible and
 * removable before anything is sent — a textarea of pasted numbers only reports
 * its mistakes after the SMS has already gone out. A paste with several numbers
 * in it is still accepted and split into chips. Nothing leaves the browser until
 * the final button, which sends the whole list in one request.
 *
 * Nobody is assigned a task here. Handing a colleague work before the owner has
 * even seen the board means picking a task for them at random; the board itself
 * asks for that, next to the tasks it is talking about. */
const InviteStep = ({ loading, result, onSubmit, onSkip, onFinish }) => {
  const [value, setValue] = useState("");
  const [mobiles, setMobiles] = useState([]);
  const [error, setError] = useState(null);

  const parsed = useMemo(() => parseMobiles(value), [value]);

  const add = () => {
    if (!value.trim()) return;

    if (!parsed.valid.length) {
      setError("شماره‌ی موبایل معتبر نیست");
      return;
    }

    const fresh = parsed.valid.filter((mobile) => !mobiles.includes(mobile));

    setMobiles([...mobiles, ...fresh]);
    setValue("");
    setError(
      fresh.length
        ? parsed.invalid.length
          ? `${toFa(parsed.invalid.length)} مورد نامعتبر بود و اضافه نشد`
          : null
        : "این شماره قبلاً اضافه شده",
    );
  };

  const remove = (mobile) =>
    setMobiles(mobiles.filter((item) => item !== mobile));

  if (result) {
    const names = result.invited
      .map((user) => user?.full_name?.trim() || user?.mobile)
      .filter(Boolean);

    return (
      <div className={styles.doneState}>
        <span className={styles.doneIcon}>
          <CheckCircleFilled />
        </span>
        <Typography.Title level={4} className={styles.stepTitle}>
          {names.length === 1
            ? `پیامک دعوت برای ${names[0]} رفت.`
            : `پیامک دعوت برای ${toFa(names.length)} نفر رفت.`}
        </Typography.Title>
        <p className={styles.stepDescription}>
          به‌محض اینکه وارد بشن توی لیست کاربرات می‌بینیشون. روی تخته نشونت
          می‌دیم چطور اولین تسک رو بهشون بسپاری.
        </p>

        {result.skipped?.length ? (
          <Alert
            type="info"
            showIcon
            className={styles.resultAlert}
            message={`${toFa(result.skipped.length)} شماره اضافه نشد`}
            description="یا قبلاً عضو این سازمان بودن یا شماره‌شون درست نبود."
          />
        ) : null}

        <Button type="primary" size="large" block onClick={onFinish}>
          بریم سراغ تخته
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className={styles.stepHead}>
        <Typography.Title level={4} className={styles.stepTitle}>
          کیا قراره باهات کار کنن؟
        </Typography.Title>
        <p className={styles.stepDescription}>
          شماره‌ی همکارات رو یکی‌یکی اضافه کن. تا وقتی دکمه‌ی پایین رو نزدی هیچ
          پیامکی فرستاده نمی‌شه.
        </p>
      </div>

      <Space.Compact className={styles.inviteInput}>
        <Input
          size="large"
          autoFocus
          value={value}
          placeholder="۰۹۱۲۳۴۵۶۷۸۹"
          inputMode="numeric"
          status={error && !mobiles.length ? "error" : undefined}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          onPressEnter={(event) => {
            event.preventDefault();
            add();
          }}
        />
        <Button
          size="large"
          type="primary"
          icon={<PlusOutlined />}
          onClick={add}
          disabled={!value.trim()}
        >
          افزودن
        </Button>
      </Space.Compact>

      {error ? <span className={styles.inviteError}>{error}</span> : null}

      {mobiles.length ? (
        <div className={styles.chipList}>
          {mobiles.map((mobile) => (
            <Tag key={mobile} closable onClose={() => remove(mobile)}>
              {toFa(mobile)}
            </Tag>
          ))}
        </div>
      ) : (
        <span className={styles.parseHint}>هنوز شماره‌ای اضافه نشده</span>
      )}

      <div className={styles.stepActions}>
        <Button
          type="primary"
          size="large"
          block
          icon={<MessageOutlined />}
          loading={loading}
          disabled={!mobiles.length}
          onClick={() => onSubmit(mobiles)}
        >
          {mobiles.length
            ? `ارسال دعوت برای ${toFa(mobiles.length)} نفر`
            : "ارسال دعوت"}
        </Button>
        <Button type="text" className={styles.skipButton} onClick={onSkip}>
          فعلاً تنها کار می‌کنم
        </Button>
      </div>
    </>
  );
};

export default InviteStep;
